import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useFirebasePhoneAuth } from "@/lib/usePhoneAuth";
import {
  Storefront, ArrowRight, ArrowLeft, Lightning, ChartLineUp, Robot, CurrencyInr
} from "@phosphor-icons/react";

export default function SellerLogin() {
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("mobile");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const { requestOtp, confirmCode, sending, verifying, resendIn, canResend, error, reset } =
    useFirebasePhoneAuth("recaptcha-seller");

  const sendOtp = async (e) => {
    e?.preventDefault();
    try {
      await requestOtp(mobile);
      setStep("otp");
      toast.success(`OTP sent to +91 ${mobile}`);
    } catch (err) {
      toast.error(err.message || "Failed to send OTP");
    }
  };

  const resend = async () => {
    if (!canResend) return;
    try { await requestOtp(mobile); toast.success("OTP sent again"); }
    catch (err) { toast.error(err.message || "Could not resend OTP"); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    try {
      const { idToken } = await confirmCode(otp);
      const r = await api.post("/auth/verify-otp", { id_token: idToken, role: "shopkeeper", name });
      login(r.data.token, r.data.user, r.data.active_role || "shopkeeper");
      toast.success(`Welcome${r.data.user.name ? `, ${r.data.user.name}` : ""}!`);

      const role = r.data.active_role || r.data.user.role;
      reset();
      if (role === "super_admin") nav("/super-admin");
      else if (role === "shopkeeper") nav(r.data.user.store_id ? "/admin" : "/onboard");
      else nav("/home");
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || "Invalid OTP");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-5 bg-[#F9F9F9]">
      <div id="recaptcha-seller" className="fixed bottom-0 left-0 pointer-events-none" />
      {/* Left brand panel (SaaS-style) */}
      <aside className="hidden lg:flex lg:col-span-2 bg-gray-900 text-white p-10 flex-col justify-between relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#F8CB46]/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#15803D]/30 blur-3xl" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-12">
            <div className="w-10 h-10 rounded-xl bg-[#F8CB46] flex items-center justify-center">
              <Lightning weight="fill" size={22} className="text-black" />
            </div>
            <span className="font-heading text-2xl font-black tracking-tight">QuickDukaan</span>
          </div>

          <div className="inline-flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5">
            <Storefront weight="fill" size={14} className="text-[#F8CB46]" />
            Seller portal
          </div>
          <h2 className="font-heading text-4xl font-black tracking-tight leading-[1.1]">
            Run a 10-minute<br />delivery business.
          </h2>
          <p className="text-gray-400 mt-4 max-w-sm">
            Manage products, orders, pincode-level ETA, delivery partners and subscriptions — all in one place.
          </p>
        </div>

        <div className="relative space-y-3">
          {[
            { icon: Robot, label: "AI fills product details from a photo" },
            { icon: ChartLineUp, label: "Real-time revenue & repeat customer insights" },
            { icon: CurrencyInr, label: "Direct payouts via Razorpay — 0% setup" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                <f.icon weight="duotone" size={18} className="text-[#F8CB46]" />
              </div>
              <span className="text-gray-200">{f.label}</span>
            </div>
          ))}
        </div>
      </aside>

      {/* Right form */}
      <main className="lg:col-span-3 flex flex-col">
        <div className="p-4">
          <Link to="/QDplatform" className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700" data-testid="seller-login-back">
            <ArrowLeft size={18} /> About QuickDukaan
          </Link>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md fade-up">
            <div className="mb-6">
              <div className="inline-flex items-center gap-2 bg-[#F8CB46]/15 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3 text-black">
                <Storefront weight="fill" size={12} /> For business owners
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">
                {step === "mobile" ? "Seller login" : "Verify your number"}
              </h1>
              <p className="text-gray-600 mt-2">
                {step === "mobile" ? "Login to your QuickDukaan dashboard or create a new store." : `OTP sent to +91 ${mobile}`}
              </p>
            </div>

            <div className="qd-card p-6">
              {step === "mobile" ? (
                <form onSubmit={sendOtp} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Your name (new sellers)</label>
                    <input data-testid="seller-name-input"
                      className="qd-input mt-2"
                      value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Rohit Sharma" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Mobile number</label>
                    <div className="mt-2 relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-base pointer-events-none select-none border-r border-gray-300 pr-3">+91</span>
                      <input data-testid="seller-mobile-input"
                        type="tel" inputMode="numeric" maxLength={10}
                        className="qd-input pl-[68px] font-mono text-lg"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                        placeholder="9876543210" />
                    </div>
                  </div>
                  {error && <div className="text-xs text-red-600">{error}</div>}
                  <button type="submit" disabled={sending} data-testid="seller-send-otp-btn"
                    className="qd-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {sending ? "Sending…" : "Continue"} <ArrowRight weight="bold" size={18} />
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-1">
                    Sellers across India can sign up free.
                  </p>
                  <div className="pt-4 border-t border-gray-100 text-center">
                    <Link to="/login" className="text-xs font-semibold text-gray-600 hover:text-black" data-testid="seller-go-customer">
                      Just want to shop? <span className="text-[#15803D]">Customer login →</span>
                    </Link>
                  </div>
                </form>
              ) : (
                <form onSubmit={verifyOtp} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-gray-500">6-Digit OTP</label>
                    <input data-testid="seller-otp-input"
                      type="tel" inputMode="numeric" autoFocus maxLength={6}
                      className="qd-input mt-2 font-mono text-2xl tracking-[0.5em] text-center"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="••••••" />
                  </div>
                  <div className="text-xs text-center text-gray-500 bg-[#F8CB46]/10 rounded-lg p-2">
                    {name ? `New here? You'll be guided to create your store after verification.` : `Existing seller? You'll be taken to your dashboard.`}
                  </div>
                  {error && <div className="text-xs text-red-600" data-testid="seller-otp-error">{error}</div>}
                  <button type="submit" disabled={submitting || verifying} data-testid="seller-verify-otp-btn"
                    className="qd-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {submitting || verifying ? "Verifying…" : "Verify & continue"}
                  </button>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <button type="button" className="underline"
                      onClick={() => { reset(); setOtp(""); setStep("mobile"); }} data-testid="seller-change-number">
                      Change number
                    </button>
                    {canResend ? (
                      <button type="button" onClick={resend} disabled={sending}
                        className="text-[#15803D] font-bold" data-testid="seller-resend-otp">
                        Resend OTP
                      </button>
                    ) : (
                      <span data-testid="seller-resend-timer">Resend in {resendIn}s</span>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
